import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield,
  ArrowLeft,
  AlertCircle,
  CheckSquare,
  Square,
  ChevronDown,
  FileText,
  CheckCircle2,
  Info,
} from 'lucide-react';
import { createPageUrl } from '../../utils';
import apiClient from '../../api/apiClient';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { useToast } from '../../context/ToastContext';

interface Role {
  id: number;
  name: string;
  code: string;
  description?: string;
}

interface Permission {
  id: number;
  permission_name: string;
  permission_key: string;
  action_type: string;
  module_name?: string;
  module_code?: string;
}

// interface ModuleData {
//   code: string;
//   name: string;
//   permissions: Permission[];
// }

export default function RoleAccessDetail() {
  const [searchParams] = useSearchParams();
  const roleId = searchParams.get('id');
  const mode = searchParams.get('mode') || 'view';
  const isEditMode = mode === 'edit';

  const { showSuccess, showError } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [role, setRole] = useState<Role | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<Set<number>>(new Set());
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [orgData, permissionsData] = await Promise.all([
        apiClient.get('/hris/organization'),
        apiClient.get(`/hris/role-permissions/features/all`).catch(() => ({ features: [] })),
      ]);

      // Find the role
      const foundRole = (orgData.roles || []).find((r: any) => r.id === Number(roleId));
      if (foundRole) {
        setRole(foundRole);
      }

      // Load role permissions
      const rolePermsResponse = await apiClient
        .get(`/hris/role-permissions/${roleId}`)
        .catch(() => ({ permissions: [] }));
      const rolePermissions = rolePermsResponse.permissions || [];

      // Set all permissions (backend returns 'features' not 'permissions')
      const allPermissions = permissionsData.features || [];
      setPermissions(allPermissions);

      // Set selected permission IDs
      const selectedIds = new Set<number>(
        rolePermissions
          .map((rp: any) => Number(rp.permission_id || rp.feature_id || rp.id))
          .filter((id): id is number => !isNaN(id))
      );
      setSelectedPermissionIds(selectedIds);

      // Expand all modules by default
      if (allPermissions.length > 0) {
        const allModuleKeys = ['HRIS', 'CREDIT', 'DEBTOR', 'ARCHIVE'];
        setExpandedModules(new Set(allModuleKeys));
      }
    } catch (error) {
      showError('Gagal memuat data role');
    } finally {
      setLoading(false);
    }
  }, [roleId, showError]);

  useEffect(() => {
    if (roleId) {
      loadData();
    }
  }, [roleId, loadData]);

  const getModuleFromPermissionKey = (key: string): { code: string; name: string } => {
    const parts = key.split('.');
    if (parts.length > 0) {
      const moduleCode = parts[0].toUpperCase();

      // All HRIS variants (HRIS, HRIS_EMP, HRIS_ORG, etc.) grouped into HRIS
      if (moduleCode.startsWith('HRIS')) {
        return { code: 'HRIS', name: 'Human Resource Information System' };
      }

      // All CREDIT variants (CREDIT, CREDIT_SUBMISSION, CREDIT_APPLICATION, etc.) grouped into CREDIT
      if (moduleCode.startsWith('CREDIT')) {
        return { code: 'CREDIT', name: 'Analisis Kredit' };
      }

      // DEBTOR variants (DEBTOR, DEBTOR_ARCHIVE, DEBTOR_DOCUMENT, etc.) grouped into DEBTOR
      if (moduleCode.startsWith('DEBTOR')) {
        return { code: 'DEBTOR', name: 'Arsip Debitur' };
      }

      // ARCHIVE variants (ARCHIVE_GENERAL, ARCHIVE_CATEGORY, etc.) grouped into ARCHIVE
      if (moduleCode.startsWith('ARCHIVE')) {
        return { code: 'ARCHIVE', name: 'Arsip Terintegrasi' };
      }

      // Map module codes to display names (fallback)
      const moduleMap: { [key: string]: { code: string; name: string } } = {
        CREDIT: { code: 'CREDIT', name: 'Analisis Kredit' },
        DEBTOR: { code: 'DEBTOR', name: 'Arsip Debitur' },
        ARCHIVE: { code: 'ARCHIVE', name: 'Arsip Terintegrasi' },
      };

      return moduleMap[moduleCode] || { code: moduleCode, name: moduleCode };
    }
    return { code: 'OTHER', name: 'Other' };
  };

  const groupedPermissions = useMemo(() => {
    const grouped: { [key: string]: { code: string; name: string; permissions: Permission[] } } =
      {};

    // Desired module order: HRIS, CREDIT, DEBTOR, ARCHIVE
    const moduleOrder = ['HRIS', 'CREDIT', 'DEBTOR', 'ARCHIVE'];
    const moduleMap: { [key: string]: { code: string; name: string } } = {
      HRIS: { code: 'HRIS', name: 'Human Resource Information System' },
      CREDIT: { code: 'CREDIT', name: 'Analisis Kredit' },
      DEBTOR: { code: 'DEBTOR', name: 'Arsip Debitur' },
      ARCHIVE: { code: 'ARCHIVE', name: 'Arsip Terintegrasi' },
    };

    // Initialize with correct order
    moduleOrder.forEach(code => {
      const moduleKey = code;
      grouped[moduleKey] = {
        code: moduleMap[code].code,
        name: moduleMap[code].name,
        permissions: [],
      };
    });

    // Group permissions
    permissions.forEach(permission => {
      const module = getModuleFromPermissionKey(permission.permission_key);
      const moduleKey = module.code;

      if (grouped[moduleKey]) {
        grouped[moduleKey].permissions.push(permission);
      } else {
        // If module is not recognized, add to OTHER
        if (!grouped['OTHER']) {
          grouped['OTHER'] = { code: 'OTHER', name: 'Other', permissions: [] };
        }
        grouped['OTHER'].permissions.push(permission);
      }
    });

    // Don't remove main modules (HRIS, CREDIT, DEBTOR, ARCHIVE) even if empty
    // Only remove OTHER module if empty
    if (grouped['OTHER'] && grouped['OTHER'].permissions.length === 0) {
      delete grouped['OTHER'];
    }

    return grouped;
  }, [permissions]);

  const handleTogglePermission = (permissionId: number) => {
    if (!isEditMode) return;

    setSelectedPermissionIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(permissionId)) {
        newSet.delete(permissionId);
      } else {
        newSet.add(permissionId);
      }
      return newSet;
    });
  };

  const handleToggleModule = (moduleName: string) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(moduleName)) {
        newSet.delete(moduleName);
      } else {
        newSet.add(moduleName);
      }
      return newSet;
    });
  };

  const handleSelectAllInModule = (moduleKey: string) => {
    if (!isEditMode) return;

    const moduleData = groupedPermissions[moduleKey];
    if (!moduleData || moduleData.permissions.length === 0) return;

    const modulePermissionIds = moduleData.permissions.map(p => p.id);
    const allSelected = modulePermissionIds.every(id => selectedPermissionIds.has(id));

    setSelectedPermissionIds(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        // Unselect all in this module
        modulePermissionIds.forEach(id => newSet.delete(id));
      } else {
        // Select all in this module
        modulePermissionIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    if (!roleId) return;

    try {
      setSaving(true);
      // Transform selected permission IDs to the format expected by backend
      const permissionsToSave = Array.from(selectedPermissionIds).map(permissionId => {
        const permission = permissions.find(p => p.id === permissionId);
        return {
          feature_id: permissionId,
          module_id: (permission as any)?.module_id || null,
          permissions: [], // Empty array for now
          access_scope: 'SELF',
          condition_rule: {},
        };
      });

      await apiClient.put(`/hris/role-permissions/${roleId}`, {
        permissions: permissionsToSave,
      });
      showSuccess('Permissions berhasil disimpan');
    } catch (error) {
      showError('Gagal menyimpan permissions');
    } finally {
      setSaving(false);
    }
  };

  const getActionFromKey = (key: string): string => {
    const parts = key.split('.');
    if (parts.length > 0) {
      return parts[parts.length - 1].toUpperCase();
    }
    return 'VIEW';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-4 w-32 mb-2" />
          <Skeleton className="h-9 w-48" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-64 mt-2" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!role) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Shield className="w-16 h-16 text-gray-300 mb-4" />
        <p className="text-gray-500 text-lg">Role tidak ditemukan</p>
        <Link to={createPageUrl('RoleAccessManagement')}>
          <Button variant="outline" className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali ke Daftar Role
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <Link to={createPageUrl('RoleAccessManagement')} className="hover:text-gray-700">
            Role
          </Link>
          <span>/</span>
          <span>{isEditMode ? 'Ubah' : 'Lihat'}</span>
        </div>
        <h1 className="text-3xl font-bold">{isEditMode ? 'Ubah' : 'Lihat'} Role</h1>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-6">
        {/* Left: Role Information */}
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="text-lg">Informasi Role</CardTitle>
            <p className="text-sm text-gray-500">Role yang sudah disediakan untuk tenant Anda</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm text-gray-600">Nama Role</Label>
              <Input value={role.name} disabled className="bg-gray-50" />
              <p className="text-xs text-gray-500">Nama role tidak bisa diubah</p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm text-gray-600">Kode Role</Label>
              <Input value={role.code} disabled className="bg-gray-50 font-mono" />
            </div>

            <div className="pt-4 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Total Permissions</span>
                <Badge className="bg-green-500 text-white font-semibold px-3 py-1">
                  {selectedPermissionIds.size}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Right: Permissions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Hak Akses (Permissions)</CardTitle>
            <p className="text-sm text-gray-500">
              {isEditMode
                ? 'Atur permissions untuk role ini sesuai kebutuhan tenant Anda'
                : 'Daftar permissions yang diberikan ke role ini'}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Edit Mode Info Banner */}
            {isEditMode && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-3">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Info className="w-5 h-5 text-blue-600" />
                  <CheckCircle2 className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700">
                    Centang permission yang ingin diberikan ke role ini
                  </p>
                </div>
              </div>
            )}

            {/* View Mode Banner */}
            {!isEditMode && (
              <div className="bg-gray-100 border border-gray-200 rounded-lg p-3 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Mode view</span> - Anda hanya dapat melihat
                    permissions. Klik "Ubah" untuk mengedit.
                  </p>
                </div>
              </div>
            )}

            {/* Permissions by Module */}
            {Object.keys(groupedPermissions).length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Tidak ada permissions tersedia</p>
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(groupedPermissions)
                  .sort(([keyA], [keyB]) => {
                    // Sort: HRIS, CREDIT, DEBTOR, ARCHIVE, then others
                    const order = ['HRIS', 'CREDIT', 'DEBTOR', 'ARCHIVE'];
                    const indexA = order.indexOf(keyA);
                    const indexB = order.indexOf(keyB);
                    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                    if (indexA !== -1) return -1;
                    if (indexB !== -1) return 1;
                    return 0;
                  })
                  .map(([moduleKey, moduleData]) => {
                    const isExpanded = expandedModules.has(moduleKey);
                    return (
                      <div
                        key={moduleKey}
                        className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm"
                      >
                        <button
                          type="button"
                          onClick={() => handleToggleModule(moduleKey)}
                          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <div className="bg-blue-100 text-blue-700 text-xs font-bold tracking-wider px-2 py-1 rounded-lg border border-blue-200 inline-flex items-center">
                              {moduleData.code}
                            </div>
                            <span className="font-semibold text-sm text-gray-900">
                              {moduleData.name}
                            </span>
                          </div>
                          <ChevronDown
                            className={`w-4 h-4 text-gray-500 transition-transform ${
                              isExpanded ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3, ease: 'easeInOut' }}
                              className="overflow-hidden"
                            >
                              <div className="p-4 bg-white border-t border-gray-200">
                                <motion.div
                                  initial={{ y: -10, opacity: 0 }}
                                  animate={{ y: 0, opacity: 1 }}
                                  transition={{ delay: 0.1 }}
                                  className="flex items-center justify-between mb-3"
                                >
                                  <p className="text-sm font-semibold text-gray-700">
                                    {moduleData.name}
                                  </p>
                                  {isEditMode && moduleData.permissions.length > 0 && (
                                    <button
                                      type="button"
                                      onClick={() => handleSelectAllInModule(moduleKey)}
                                      className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                                    >
                                      {(() => {
                                        const modulePermissionIds = moduleData.permissions.map(
                                          p => p.id
                                        );
                                        const allSelected = modulePermissionIds.every(id =>
                                          selectedPermissionIds.has(id)
                                        );
                                        return allSelected ? (
                                          <>
                                            <CheckSquare className="w-4 h-4" />
                                            <span>Pilih Semua</span>
                                          </>
                                        ) : (
                                          <>
                                            <Square className="w-4 h-4" />
                                            <span>Pilih Semua</span>
                                          </>
                                        );
                                      })()}
                                    </button>
                                  )}
                                </motion.div>
                                {moduleData.permissions.length === 0 ? (
                                  <motion.p
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.2 }}
                                    className="text-sm text-gray-500 text-center py-4"
                                  >
                                    Belum ada permissions untuk module ini
                                  </motion.p>
                                ) : (
                                  <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.15, staggerChildren: 0.05 }}
                                    className="grid grid-cols-2 gap-3"
                                  >
                                    {moduleData.permissions.map((permission, index) => {
                                      const isSelected = selectedPermissionIds.has(permission.id);
                                      const actionType = getActionFromKey(
                                        permission.permission_key
                                      );

                                      return (
                                        <motion.button
                                          key={permission.id}
                                          initial={{ opacity: 0, scale: 0.95 }}
                                          animate={{ opacity: 1, scale: 1 }}
                                          transition={{ delay: 0.2 + index * 0.03 }}
                                          type="button"
                                          onClick={() => handleTogglePermission(permission.id)}
                                          disabled={!isEditMode}
                                          whileHover={isEditMode ? { scale: 1.02 } : {}}
                                          whileTap={isEditMode ? { scale: 0.98 } : {}}
                                          className={`flex items-start gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                                            isSelected
                                              ? 'border-blue-500 bg-blue-50'
                                              : 'border-gray-200 hover:border-gray-300 bg-white'
                                          } ${!isEditMode ? 'cursor-default' : 'cursor-pointer'}`}
                                        >
                                          {isSelected ? (
                                            <CheckSquare className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                          ) : (
                                            <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                          )}
                                          <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-sm text-gray-900 mb-1">
                                              {permission.permission_name}
                                            </p>
                                            <p className="text-xs text-gray-500 truncate mb-2 font-mono">
                                              {permission.permission_key}
                                            </p>
                                            <Badge
                                              variant="outline"
                                              className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 border-gray-300"
                                            >
                                              {actionType}
                                            </Badge>
                                          </div>
                                        </motion.button>
                                      );
                                    })}
                                  </motion.div>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
              </div>
            )}

            {/* Action Buttons */}
            {isEditMode && (
              <div className="flex items-center justify-between pt-4 border-t mt-4">
                <Link to={createPageUrl('RoleAccessManagement')}>
                  <Button variant="outline" className="gap-2">
                    <ArrowLeft className="w-4 h-4" />
                    Kembali
                  </Button>
                </Link>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="gap-2 bg-blue-600 hover:bg-blue-700"
                >
                  <FileText className="w-4 h-4" />
                  {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
